/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { Pressable, View, Icon, BoxSpinnerView, BoxEmptyView } from '@components/atoms'
import { AccessControlMenuList } from '@components/systems/accessControl/AccessControlMenuList'
import { Collapse } from '@mui/material'
import { mono, boFont } from '@components/systems/backoffice/backoffice.tokens'

import {
	useAppSelector,
	ApiGetDataStatus,
	MenuTreeNodeType,
	MenuType,
} from '@redux/index'
import { MenuTreeNode, AccessControlType, AccessGroupType } from '@schemas'

interface PropsType {
	status: ApiGetDataStatus
	accessControlList: AccessControlType[]
	isCollapsible?: boolean
	isSelectedGroup?: boolean
	detailItemStyle?: React.CSSProperties
	isShownDeleteButton?: boolean
	onDeleteClick?: (accessControlId: string) => void
}

/** 메뉴 권한 표 — 섹션(메인/어드민)은 키커 행, 안은 헤어라인 행 */
export const AccessControlView = (props: PropsType) => {
	const { status, accessControlList, isSelectedGroup } = props

	// redux state
	const selectedTab: string = useAppSelector(state => state.accessControl.selectedTab)
	const mainMenuTreeList: MenuTreeNodeType[] = useAppSelector(
		state => state.admin.menuTreeList,
	)
	const adminMenu: MenuType[] = useAppSelector(state => state.admin.menu)
	const selectedGroup: AccessGroupType = useAppSelector(
		state => state.accessControl.selectedAccessGroup,
	)

	// variables
	const isDisabled: boolean = selectedGroup.id === ''
	const adminMenuTreeList: MenuTreeNodeType[] = React.useMemo(() => {
		const res = adminMenu.map((anAdminMenu, index) => {
			return new MenuTreeNode({
				...anAdminMenu,
				text: anAdminMenu.name,
				droppable: false,
				isActivate: true,
				order: index,
				parent: 'root',
				i18n: [],
			})
		})

		return res
	}, [adminMenu])

	const defaultMenuSectionList: ('main' | 'admin')[] = ['main', 'admin']

	// state
	const [collapsedList, setCollapsedList] =
		React.useState<string[]>(defaultMenuSectionList)

	const onMenuSectionClick = (sectionName: 'main' | 'admin') => {
		const newCollapsedList = [...collapsedList]
		const index: number = newCollapsedList.indexOf(sectionName)
		if (index > -1) {
			newCollapsedList.splice(index, 1)
		} else {
			newCollapsedList.push(sectionName)
		}

		setCollapsedList(newCollapsedList)
	}

	return (
		<View css={[style]}>
			<View className="accessControlInnerView">
				{status === 'init' ||
					(status === 'loading' && (
						<View className="spinnerView">
							<BoxSpinnerView />{' '}
						</View>
					))}

				{selectedTab === 'user' && isSelectedGroup !== undefined && !isSelectedGroup ? (
					<BoxEmptyView isGrey={true} title={'그룹을 선택해주세요.'} />
				) : (
					<>
						{defaultMenuSectionList.map(aSection => {
							const isCollapseOpend: boolean = collapsedList.includes(aSection)
							return (
								<View key={aSection} className="menuListOuter">
									<View className="menuSectionView">
										<Pressable
											className={`menuSectionButton ${isCollapseOpend ? 'open' : ''}`}
											onClick={() => onMenuSectionClick(aSection)}
										>
											<span className="menuSectionTitle">
												{aSection === 'main' ? '메인 메뉴' : '어드민 메뉴'}
											</span>
											<Icon className="menuSectionChevronIcon" name="chevron-right" />
										</Pressable>
									</View>

									<Collapse in={collapsedList.includes(aSection)} timeout="auto">
										<AccessControlMenuList
											accessControlList={accessControlList}
											menuTreeList={
												aSection === 'main' ? mainMenuTreeList : adminMenuTreeList
											}
											isDisabled={isDisabled}
										/>
									</Collapse>
								</View>
							)
						})}
					</>
				)}
			</View>
		</View>
	)
}

const style = css({
	width: '100%',
	height: '100%',
	overflow: 'auto',
	fontFamily: boFont.ui,

	'.accessControlInnerView': {
		position: 'relative',
		width: '100%',
		height: '100%',

		'.spinnerView': {
			position: 'absolute',
			zIndex: 9,
			width: '100%',
			height: '100%',
			top: 0,
			background: mono.bg,
			opacity: 0.6,
		},

		'.menuListOuter': {
			marginBottom: 24,

			'.menuSectionView': {
				width: '100%',

				'.menuSectionButton': {
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					width: '100%',
					height: 36,
					padding: 0,
					borderBottom: `1px solid ${mono.line}`,

					'.menuSectionTitle': {
						fontSize: 11,
						letterSpacing: '.24em',
						textTransform: 'uppercase',
						fontWeight: 500,
						color: mono.faint,
						transition: 'color 200ms ease-out',
					},

					'.menuSectionChevronIcon': {
						fontSize: 16,
						color: mono.faint,
						transition: 'transform 200ms ease-out',
					},

					'&.open .menuSectionChevronIcon': { transform: 'rotate(90deg)' },
					'&:hover .menuSectionTitle': { color: mono.ink },
				},
			},
		},
	},
})
